import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Grid,
  Card,
  CardContent,
  Chip,
  useMediaQuery,
  LinearProgress,
  Alert,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  InputAdornment,
} from "@mui/material";
import { GRAPHQL_URL } from '../../config/api';
import { BUSINESS_CONSTANTS } from '../../config/businessConstants';
import { ensureCsrfToken, getCsrfTokenKeyByEnv } from '../../utils/csrf';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Nav from "../common/Nav.jsx";
import FAQSection from "../common/FAQSection.jsx";
import { useAuth } from "../common/AuthContext.jsx";
import { extractAndStoreCsrfToken } from "../../utils/csrf.js";
import { extractApiErrorMessage } from "../../utils/apiErrorMessage.js";
import { getAuthToken } from "../../utils/auth.js";
import { apiFetch } from "../../utils/api";
import TransactionHistory from "../transaction/TransactionHistory.jsx";

const countries = [
  { name: "India", code: "IN", phone: 91, flag: "🇮🇳" },
];

const CustomerForm = () => {
  const navigate = useNavigate();
  const { isAuthenticated, setIsAuthenticated } = useAuth();
  const otpLength = 6;
  const otpRefs = useRef([]);

  const [otpState, setOtpState] = useState(false);
  const [timer, setTimer] = useState(120);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [otp, setOtp] = useState(Array(otpLength).fill(""));

  const [verifyOtpPage, setVerifyOtpPage] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpVerificationStatus, setOtpVerificationStatus] = useState(null);
  const [custForm, setCustForm] = useState(true);

  // Set selectedCountry to India by default
  const [selectedCountry, setSelectedCountry] = useState(countries[0]);
  const [numberInfo, setNumberInfo] = useState({
    number: "",
    submitted: false,
  });
  const [paymentType, setPaymentType] = useState("mobile");
  const [mobileError, setMobileError] = useState("");
  const [isMobileValid, setIsMobileValid] = useState(false);

  const [openCountryList, setOpenCountryList] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");

  const [otpKey, setOtpKey] = useState("");
  const [formLoading, setFormLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [otpError, setOtpError] = useState("");

  const flagRef = useRef(null);

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const isLocalhost = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

  // Redirect if already authenticated and token still exists
  useEffect(() => {
    if (isAuthenticated && getAuthToken()) {
      navigate('/transaction-history');
    }
  }, [isAuthenticated, navigate]);

  // Clear transactionId flow flag when returning to home page
  useEffect(() => {
    sessionStorage.removeItem('paymentIdFlow');
    sessionStorage.removeItem('lastPaymentId');
  }, []);

  useEffect(() => {
    let countdown;
    if (otpState && timer > 0) {
      countdown = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setIsResendDisabled(false);
      clearInterval(countdown);
    }
    return () => clearInterval(countdown);
  }, [otpState, timer]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  const mobileValidationRules = {
    IN: { minLength: 10, maxLength: 10, pattern: /^[6-9]\d{9}$/ },
    US: { minLength: 10, maxLength: 10, pattern: /^\d{10}$/ },
    GB: { minLength: 10, maxLength: 11, pattern: /^(\+44|0)?[1-9]\d{9}$/ },
    CA: { minLength: 10, maxLength: 10, pattern: /^\d{10}$/ },
    AU: { minLength: 9, maxLength: 9, pattern: /^[4]\d{8}$/ },
    DE: { minLength: 10, maxLength: 12, pattern: /^(\+49|0)?[1-9]\d{8,10}$/ },
    NG: { minLength: 11, maxLength: 11, pattern: /^[0-9]\d{10}$/ },
  };
  const validateMobileNumber = (number, countryCode) => {
    const cleanNumber = number.replace(/\s+/g, "");

    if (!cleanNumber) {
      return { isValid: false, error: "Mobile number is required." };
    }

    if (!/^\d+$/.test(cleanNumber)) {
      return {
        isValid: false,
        error: "Mobile number should contain only digits.",
      };
    }

    // For Indian mobile numbers, restrict to exactly 10 digits
    if (countryCode === "IN") {
      if (cleanNumber.length !== 10) {
        return {
          isValid: false,
          error: "Indian mobile number should be exactly 10 digits.",
        };
      }
      
      if (!/^[6-9]/.test(cleanNumber)) {
        return {
          isValid: false,
          error: "Indian mobile number should start with 6, 7, 8, or 9",
        };
      }
      
      return { isValid: true, error: "" };
    }

    // For other countries, use existing rules
    const rules = mobileValidationRules[countryCode];
    if (!rules) {
      if (cleanNumber.length < 7 || cleanNumber.length > 15) {
        return {
          isValid: false,
          error: "Mobile number should be between 7-15 digits.",
        };
      }
      return { isValid: true, error: "" };
    }

    if (
      cleanNumber.length < rules.minLength ||
      cleanNumber.length > rules.maxLength
    ) {
      const countryName = countries.find((c) => c.code === countryCode)?.name;
      if (rules.minLength === rules.maxLength) {
        return {
          isValid: false,
          error: `Mobile number should be ${rules.minLength} digits for ${countryName}`,
        };
      } else {
        return {
          isValid: false,
          error: `Mobile number should be ${rules.minLength}-${rules.maxLength} digits for ${countryName}`,
        };
      }
    }

    if (!rules.pattern.test(cleanNumber)) {
      let countryName = countries.find((c) => c.code === countryCode)?.name;
      switch (countryCode) {
        case "AU":
          return {
            isValid: false,
            error: "Australian mobile number should start with 4",
          };
        default:
          return {
            isValid: false,
            error: `Invalid mobile number format for ${countryName}`,
          };
      }
    }

    return { isValid: true, error: "" };
  };

  const formatMobileNumber = (number) => {
    const cleanNumber = number.replace(/\D/g, "");

    switch (selectedCountry.code) {
      case "IN":
        return cleanNumber.replace(/(\d{5})(\d{5})/, "$1 $2");
      case "US":
      case "CA":
        return cleanNumber.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
      case "GB":
        return cleanNumber.replace(/(\d{5})(\d{6})/, "$1 $2");
      default:
        return cleanNumber.replace(/(\d{4})(?=\d)/g, "$1 ");
    }
  };

  const sendOtp = async (phone) => {
    const fallback = "Failed to send OTP. Please try again.";
    try {
      // Ensure CSRF token is valid before making the request
      const csrfToken = await ensureCsrfToken();

      const response = await apiFetch(GRAPHQL_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [BUSINESS_CONSTANTS.SESSION_OTP_REQ_SOURCE_HEADER]:
            BUSINESS_CONSTANTS.SESSION_OTP_REQ_SOURCE.GRIEVANCE_MOBILE,
            // Only include the CSRF header when a token is available
          // to avoid sending 'X-CSRF-TOKEN: ' with an empty value
          ...(csrfToken ? { [getCsrfTokenKeyByEnv()]: csrfToken } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          query: `mutation($input: SendSessionOtpInput!){
              sendSessionOtp(input: $input){
                  status
                  statusCode
                  errorCode
                  message
                  data{
                      otpKey
                      resendReadyTime
                      source
                      sourceType
                      user
                  }
              }
          }`,
          variables: {
            input: {
              phone: phone,
              sendCommunication: true,
            },
          },
        }),
      });

      // Capture any CSRF token the server returns in this response.
      // sendOtp is the first authenticated call, so the server may issue
      // the CSRF token here for use in all subsequent requests.
      extractAndStoreCsrfToken(response);

      let json = null;
      try {
        json = await response.json();
      } catch {
        json = null;
      }

      const result = json?.data?.sendSessionOtp;
      const gqlError = json?.errors?.[0];

      if (result?.status === "OK" && result?.statusCode === 200) {
        setOtpKey(result.data.otpKey);
        setOtpState(true);
        setTimer(result.data.resendReadyTime || 120);
        setIsResendDisabled(true);
        setOtpError("");
        return { success: true, resendReadyTime: result.data.resendReadyTime };
      }

      const msg =
        extractApiErrorMessage(result, fallback) ||
        gqlError?.message ||
        fallback;
      throw new Error(msg);
    } catch (error) {
      if (error instanceof Error && error.message) {
        throw error;
      }
      throw new Error(fallback);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (paymentType === "mobile") {
      if (!numberInfo.number.trim()) {
        setMobileError("Mobile number is required");
        return;
      }

      const validation = validateMobileNumber(
        numberInfo.number,
        selectedCountry.code
      );
      if (!validation.isValid) {
        setMobileError(validation.error);
        return;
      }
    }


    setFormLoading(true);
    setOtpError("");

    if (paymentType === "mobile") {
      try {
        const otpSent = await sendOtp(numberInfo.number);
        setTimer(otpSent.resendReadyTime);
      } catch (error) {
        setFormLoading(false);
        setOtpError(error instanceof Error ? error.message : "Failed to send OTP. Please try again.");
        return;
      }
    }

    setFormLoading(false);
    setVerifyOtpPage(true);
    setCustForm(false);
    setOtpState(true);
    setIsResendDisabled(true);
  };

  const handleResendOtp = async () => {
    if (paymentType === "mobile") {
      setFormLoading(true);
      setOtpError("");
      try {
        const otpSent = await sendOtp(numberInfo.number);
        setTimer(otpSent.resendReadyTime);
        setIsResendDisabled(true);
        // Clear previously entered OTP digits so the user starts fresh.
        setOtp(Array(otpLength).fill(""));
      } catch (error) {
        setOtpError(error.message);
      } finally {
        setFormLoading(false);
      }
    }
  };

  const handleRadioChange = (event) => {
    setPaymentType(event.target.value);
    setNumberInfo({ ...numberInfo, number: "" });
    setMobileError("");
    setIsMobileValid(false);
  };

  const handlePhoneNumberChange = (e) => {
    const inputValue = e.target.value;

    if (paymentType === "mobile") {
      // Only allow digits
      const cleanValue = inputValue.replace(/\D/g, "");
      setOtpError("");
      setNumberInfo({ ...numberInfo, number: cleanValue });
      const cc = selectedCountry.code;
      // India: only validate after 10 digits (avoid errors while typing)
      if (cc === "IN" && cleanValue.length < 10) {
        setMobileError("");
        setIsMobileValid(false);
        return;
      }
      const validation = validateMobileNumber(cleanValue, cc);
      setMobileError(validation.error);
      setIsMobileValid(validation.isValid);
    } else {
      setNumberInfo({ ...numberInfo, number: inputValue });
      setMobileError("");
      setIsMobileValid(true);
    }
  };

  const handleSelect = (country) => {
    setSelectedCountry(country);
    setCountrySearch("");
    setOpenCountryList(false);

    if (paymentType === "mobile" && numberInfo.number) {
      if (country.code === "IN" && numberInfo.number.length < 10) {
        setMobileError("");
        setIsMobileValid(false);
        return;
      }
      const validation = validateMobileNumber(numberInfo.number, country.code);
      setMobileError(validation.error);
      setIsMobileValid(validation.isValid);
    }
  };


  const handleChange = (index, e) => {
    const { value } = e.target;
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    setOtpError(""); // Clear error on change
    if (value && index < otpLength - 1) {
      otpRefs.current[index + 1].focus();
    }
  };

  const handleBackspace = (index,e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];
      if (newOtp[index]) {
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        otpRefs.current[index - 1].focus();
        newOtp[index - 1] = "";
        setOtp(newOtp);
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();
    if (!/^\d{6}$/.test(pastedData)) return;
    setOtp(pastedData.split(""));
    otpRefs.current[otpLength - 1].focus();
  };

  const handleVerify = async () => {
    if (otp.join("").length === otpLength) {
      setIsVerifying(true);
      setOtpError("");
      try {
        // Ensure CSRF token is valid before making the request
        const csrfToken = await ensureCsrfToken();

        const response = await apiFetch(GRAPHQL_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // Only include the CSRF header when a token is available
            ...(csrfToken ? { [getCsrfTokenKeyByEnv()]: csrfToken } : {}),
          },
          credentials: 'include',
          body: JSON.stringify({
            query: `mutation VerifySessionOtp($input: VerifySessionOtpInput!) {
              verifySessionOtp(input: $input) {
                status
                statusCode
                message
                errorCode
                data {
                  accessToken
                }
              }
            }`,
            variables: {
              input: {
                phone: numberInfo.number,
                otpKey: otpKey,
                otp: otp.join(""),
              },
            },
          }),
        });
        let json;
        try {
          json = await response.json();
        } catch (jsonErr) {
          json = null;
        }
        const result = json?.data?.verifySessionOtp;
        const gqlError = json?.errors?.[0];

        if (response.ok && result?.status === "OK" && result?.statusCode === 200) {
          const accessToken = result.data?.accessToken;
          localStorage.setItem('accessToken', accessToken);
          localStorage.removeItem('preventAutoLogin');
          sessionStorage.removeItem('preventAutoLogin');
          // Capture CSRF token from verifyOtp response headers (rotated after login)
          extractAndStoreCsrfToken(response);
          setIsAuthenticated(true);
          setOtpVerified(true);
          setOtpError("");
          navigate("/transaction-history", {
            state: {
              phoneNumber: numberInfo.number,
              transactionData: null
            }
          });
        } else {
          setOtpError(
            result?.message ||
            gqlError?.message ||
            `Error: ${response.status} - OTP verification failed.`
          );
          setOtpVerificationStatus('failed');
        }
      } catch (error) {
        setOtpError("Failed to verify OTP. Please try again later.");
        setOtpVerificationStatus('failed');
      }
      setIsVerifying(false);
    } else {
      setOtpError("Invalid OTP. Please enter a 6-digit OTP.");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleVerify();
    }
  };

  const handleChangeNumber = () => {
    setVerifyOtpPage(false);
    setCustForm(true);
    setOtp(Array(otpLength).fill(""));
    setOtpState(false);
    setTimer(120);
    setIsResendDisabled(true);
    setOtpError("");
  };

  return (
    <Box
      sx={{
        background: "linear-gradient(to bottom, #E9F7FF, #DDEFFF, #FFFFFF)",
      }}
    >
      <Nav />

      <Box
        sx={{
          px: { xs: "20px", sm: "40px", md: "70px" },
          py: { xs: 1, sm: 1, md: 0.5 },
        }}
      >
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          sx={{ minHeight: "auto", position: "relative" }}
        >
          <Grid item xs={12} sm={10} md={6} lg={6}>
            

            {custForm ? (
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 2, sm: 3 },
                  mb: { xs: 1, sm: 1 },
                  mt:8,
                  width: "100%",
                  maxWidth: { xs: "360px", sm: "580px", lg: "900px" },
                  mx: "auto",
                  borderRadius: "20px",
                  boxShadow: "none",
                }}
              >
                <form onSubmit={handleSubmit}>
                  <Typography
                    sx={{
                      mb: 1,
                      fontSize: { xs: "1.25rem", sm: "1.5rem" },
                      fontWeight: 700,
                      lineHeight: "30px",
                    }}
                  >
                    Enter your phone number
                  </Typography>
                  <Typography
                    sx={{
                      mb: 3,
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                      fontWeight: 400,
                      color: "#25385899",
                    }}
                  >
                    Check the status of payments made in the last{" "}
                    <span style={{ fontWeight: 700, color: "#253858" }}>
                      2 months.
                    </span>
                  </Typography>

                  <Box sx={{ mb: 2, position: "relative" }}>
                    <TextField
                      fullWidth
                      label="Mobile Number"
                      placeholder="Enter mobile number"
                      value={numberInfo.number}
                      onChange={handlePhoneNumberChange}
                      error={!!mobileError}
                      helperText={
                        mobileError ||
                        (numberInfo.number.trim()
                          ? ""
                          : `Enter Indian mobile number`)
                      }
                      inputProps={{
                        maxLength: 10,
                        pattern: "[0-9]*",
                        inputMode: "numeric",
                        style: {
                          fontSize: "1rem",
                          fontWeight: 500,
                          letterSpacing: "0.5px",
                          color: "#253858",
                          paddingLeft: "4px",
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                pr: 1.5,
                                mr: 1,
                                borderRight: "1.5px solid #E1E5EB",
                                height: "28px",
                                userSelect: "none",
                              }}
                            >
                              <Box
                                sx={{
                                  width: 24,
                                  height: 24,
                                  borderRadius: "50%",
                                  overflow: "hidden",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  boxShadow: "0 0 0 1px rgba(0,0,0,0.06)",
                                }}
                              >
                                <img
                                  src="/Icons/flag.png"
                                  alt="India"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                  }}
                                />
                              </Box>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: "1rem",
                                  color: "#253858",
                                  lineHeight: 1,
                                }}
                              >
                                +91
                              </Typography>
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "4px",
                          backgroundColor: "#FAFBFC",
                          transition: "all 0.2s ease",
                          "& fieldset": {
                            borderColor: "#E1E5EB",
                            borderWidth: "1.5px",
                          },
                          "&:hover fieldset": {
                            borderColor: "#B7C0CC",
                          },
                          "&.Mui-focused": {
                            backgroundColor: "#FFFFFF",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: isMobileValid ? "#4CAF50" : mobileError ? "#f44336" : "#3965FB",
                            borderWidth: "2px",
                          },
                          "&.Mui-error fieldset": {
                            borderColor: "#f44336",
                          },
                        },
                        "& .MuiInputLabel-root": {
                          fontWeight: 500,
                          color: "#6B778C",
                          "&.Mui-focused": {
                            color: "#3965FB",
                          },
                        },
                        "& .MuiFormHelperText-root": {
                          marginLeft: "4px",
                          fontSize: "0.8rem",
                        },
                      }}
                    />
                  </Box>

                  {paymentType === "mobile" && otpError && (
                    <Typography
                      sx={{
                        color: "error.main",
                        mb: 2,
                        textAlign: "center",
                        fontWeight: 500,
                        fontSize: "0.875rem",
                      }}
                    >
                      {otpError}
                    </Typography>
                  )}

                  <Button
                    type="submit"
                    fullWidth
                    disabled={
                      paymentType === "mobile"
                        ? !isMobileValid || !numberInfo.number.trim() || formLoading
                        : !numberInfo.number.trim() || formLoading
                    }
                    sx={{
                      mt: 2,
                      height: "48px",
                      borderRadius: "8px",
                      backgroundColor: "#3965FB",
                      fontWeight: 500,
                      fontSize: "1rem",
                      color: "#FFFFFF",
                      textTransform: "capitalize",
                      "&:hover": { backgroundColor: "#335ecc" },
                      "&:disabled": {
                        backgroundColor: "#cccccc",
                        color: "#666666",
                      },
                    }}
                  >
                    {formLoading
                      ? paymentType === "transaction"
                        ? "Submitting..."
                        : "Sending OTP..."
                      : paymentType === "transaction"
                      ? "Submit"
                      : "Get OTP"}
                  </Button>

                  <Typography
                    sx={{
                      mt: 2,
                      textAlign: "center",
                      fontSize: "0.875rem",
                      color: "#25385899",
                    }}
                  >
                    Awaiting a response to an earlier query?{" "}
                    <span
                      style={{ color: "#0065FF", fontWeight: 500, cursor: "pointer", textDecoration: "underline" }}
                      onClick={() => navigate('/check-query', { state: { bypassAuth: true } })}
                    >
                      Track Existing Query
                    </span>
                  </Typography>
                </form>
              </Paper>
            ) : paymentType === "mobile" && verifyOtpPage && !otpVerified ? (
              <Box
                sx={{
                  position: 'relative',
                  maxWidth: { xs: "328px", sm: "580px", lg: "900px" },
                  width: '100%',
                  mx: 'auto',
                }}
              >
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 2, sm: 3 },
                  mb: { xs: 1, sm: 1 },
                  width: "100%",
                  borderRadius: "20px",
                  boxShadow: "none",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '20px', sm: '22px' },
                    color: "#253858",
                    pb: 1,
                    mb: 2,
                    borderBottom: '3px solid #556CD6',
                    display: 'inline-block',
                  }}
                >
                  6 digit OTP sent to your{" "}
                  {paymentType === "mobile"
                    ? "Mobile Number"
                    : "Transaction ID"}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ fontWeight: 400, fontSize: '14px', color: '#6B7280' }}>
                    Enter OTP sent on <strong style={{ color: '#253858' }}>{numberInfo.number}</strong>
                  </Typography>
                  <Button
                    variant="text"
                    onClick={handleChangeNumber}
                    sx={{
                      textTransform: 'none',
                      p: 0,
                      minWidth: 0,
                      fontWeight: 500,
                      fontSize: '14px',
                      color: '#556CD6',
                      flexShrink: 0,
                      ml: 1,
                      '&:hover': { background: 'none', textDecoration: 'underline' },
                    }}
                  >
                    Change
                  </Button>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: { xs: 1, sm: 2 },
                    mb: { xs: 2, sm: 1 },
                  }}
                >
                  {otp.map((digit, index) => (
                    <TextField
                      key={index}
                      type="text"
                      value={digit}
                      onChange={(e) => handleChange(index, e)}
                      onKeyDown={(e) => handleBackspace(index, e)}
                      onPaste={handlePaste}
                      inputProps={{
                        style: {
                          textAlign: "center",
                          fontSize: "1rem",
                          fontWeight: 700,
                        },
                        maxLength: 1,
                      }}
                      inputRef={(el) => (otpRefs.current[index] = el)}
                      sx={{
                        width: { xs: "48px", sm: "56px", md: "66px" },
                        height: { xs: "48px", sm: "56px" },
                      }}
                      onKeyPress={handleKeyPress}
                    />
                  ))}
                </Box>
                <Box sx={{ textAlign: 'center', mb: 2 }}>
                  {timer > 0 ? (
                    <Typography sx={{ fontSize: '13px', color: '#6B7280' }}>
                      Resend OTP in <strong>{formatTime(timer)}</strong>
                    </Typography>
                  ) : (
                    <Button
                      onClick={handleResendOtp}
                      disabled={formLoading}
                      variant="text"
                      sx={{
                        fontSize: '14px',
                        color: '#556CD6',
                        textDecoration: 'underline',
                        textTransform: 'none',
                        p: 0,
                        '&:hover': { background: 'none', color: '#3a4faa' },
                        '&:disabled': { color: '#aaa' },
                      }}
                    >
                      {formLoading ? 'Sending...' : 'Resend OTP'}
                    </Button>
                  )}
                </Box>
                {otpError && (
                  <Typography sx={{ color: 'red', mb: 1, textAlign: 'center', fontWeight: 500 }}>
                    {otpError}
                  </Typography>
                )}
                <Button
                  onClick={handleVerify}
                  onKeyDown={handleKeyPress}
                  disabled={isVerifying || otp.join("").length !== otpLength}
                  fullWidth
                  sx={{
                    mb: 1,
                    py: 1.5,
                    borderRadius: "8px",
                    backgroundColor: "#3965FB",
                    fontSize: "1rem",
                    fontWeight: 500,
                    color: "#ffffff",
                    textTransform: "capitalize",
                    "&:hover": { backgroundColor: "#335ecc" },
                    "&:disabled": {
                      backgroundColor: "#cccccc",
                      color: "#666666",
                    },
                  }}
                >
                  {isVerifying ? "Verifying..." : "Verify & Continue"}
                </Button>
                <Typography
                  sx={{
                    textAlign: "center",
                    fontSize: "0.875rem",
                    color: "#25385899",
                  }}
                >
                  Awaiting a response to an earlier query?{" "}
                  <Link
                    to="/check-query"
                    state={{ bypassAuth: true }}
                    style={{ color: "#0065FF", fontWeight: 500 }}
                  >
                    Track Existing Query
                  </Link>
                </Typography>
              </Paper>
              </Box>
            ) : otpVerified && paymentType === "mobile" ? (
              <TransactionHistory phoneNumber={numberInfo.number} />
            ) : null}
          </Grid>
        </Grid>
      </Box>

      <FAQSection />
    </Box>
  );
};

export default CustomerForm;
